import React from 'react';
import { Link } from 'react-router-dom';
import {
  ArrowRight,
  Sparkles,
  FolderOpen,
  MessageSquare,
  Eye,
  Undo2,
  FileText,
  Monitor,
  Lock,
  Zap,
  CheckCircle,
  HelpCircle,
  BookOpen,
} from 'lucide-react';
import SEOHead from '../components/SEOHead';
import Breadcrumbs from '../components/Breadcrumbs';
import { fireEvent } from '../utils/funnelEvents';

const CANONICAL = 'https://www.getsortio.com/ai-file-organizer';
const LAST_UPDATED = '2026-06-09';

const FAQ = [
  {
    question: 'What is an AI file organizer?',
    answer:
      'An AI file organizer is software that sorts, renames, and files documents using a language model instead of hand-written rules. You describe the outcome you want in plain English ("organize these by client, then by document type") and the AI decides where each file belongs based on its name, metadata, and, when you enable it, its contents. Traditional organizers like Hazel or File Juggler require you to write and maintain explicit rules for every pattern.',
  },
  {
    question: 'How does Sortio organize files with AI?',
    answer:
      'You point Sortio at a folder and type a prompt. Sortio reads each file\'s name and metadata, and document contents for text-based formats when content sorting is enabled, then proposes a plan: new names and new locations for every file. Nothing moves until you approve the preview, and every sort can be undone in one click.',
  },
  {
    question: 'Is Sortio free?',
    answer:
      'The download is free and there is a real free tier: a one-time AI trial allowance to try AI sort on real folders, plus unlimited rule-based sorting, semantic search, and file chat. Pro is $14.99/month or $99/year and unlocks a monthly AI allowance sized for heavy use, watch folders, and on-change automations. You upgrade inside the app after signing in.',
  },
  {
    question: 'Can an AI file organizer read what is inside my files?',
    answer:
      'Sortio can read the contents of text-based formats: PDFs with a text layer, Word, Excel, PowerPoint, plain text, and Markdown. Content reading is a toggle you control; with it off, sorting uses filenames and metadata only. Image-only scans with no text layer are sorted by filename and metadata, since Sortio does not perform OCR.',
  },
  {
    question: 'Do my files get uploaded somewhere?',
    answer:
      'Your files stay on your machine; Sortio moves and renames them locally. To make AI sorting decisions, extracted text and metadata are sent to the AI provider: Sortio\'s hosted AI by default (content is retained up to 30 days, then purged), or your own OpenAI or Anthropic API key, or a fully local model via Ollama if you want nothing sent to any cloud.',
  },
  {
    question: 'How many files can it organize at once?',
    answer:
      'AI sort handles up to 5,000 files per run. For larger libraries the recommended workflow is to sort in batches or to promote a recurring sort into a deterministic rule with the AI Rule Builder, which runs without per-file AI calls.',
  },
  {
    question: 'What is automatic file organization?',
    answer:
      'Automatic file organization means new files get filed without you touching them. In Sortio, you attach a rule or a saved AI prompt to a watched folder (a Pro feature); when files land in that folder, they are renamed and routed according to your instructions. The free tier runs the same sorts on demand.',
  },
  {
    question: 'Does it work on Windows and Mac?',
    answer:
      'Yes. Sortio is a desktop app for macOS and Windows, with the same prompts, rules, and behavior on both. The interface is available in English, Chinese (Simplified and Traditional), Korean, German, and Spanish.',
  },
];

const FEATURES = [
  {
    icon: MessageSquare,
    title: 'Prompt-based sorting',
    body: 'Type "sort these by client, then by year" and Sortio routes every file to match. No rule syntax, no regex.',
  },
  {
    icon: FileText,
    title: 'Reads document contents',
    body: 'For PDFs with a text layer, Word, Excel, PowerPoint, and text files, Sortio can sort by what a document actually is, not just what it happens to be named. Content reading is a toggle you control.',
  },
  {
    icon: Eye,
    title: 'Preview before apply',
    body: 'Every sort shows the full plan, old name to new name, old location to new location, before a single file moves.',
  },
  {
    icon: Undo2,
    title: 'One-click undo',
    body: 'Sort history records every move. Revert an entire run if the result is not what you wanted.',
  },
  {
    icon: Zap,
    title: 'Rules for the repeatable stuff',
    body: 'The AI Rule Builder turns a plain-English description into a deterministic rule that runs forever and does not use your AI allowance at all. Rule-based sorting is free and unlimited.',
  },
  {
    icon: Monitor,
    title: 'Mac and Windows',
    body: 'Same app, same prompts, same rules on macOS and Windows. UI localized in six languages.',
  },
  {
    icon: Lock,
    title: 'You choose the AI',
    body: 'Sortio\'s hosted AI by default, your own OpenAI or Anthropic key, or a fully local model via Ollama for folders that should never touch a cloud.',
  },
  {
    icon: FolderOpen,
    title: 'Watch folders and schedules',
    body: 'On Pro, attach a rule or saved prompt to a folder and new files get filed as they arrive. Schedule nightly cleanup runs.',
  },
];

const softwareSchema = {
  '@context': 'https://schema.org',
  '@type': 'SoftwareApplication',
  name: 'Sortio',
  description:
    'AI file organizer for Mac and Windows. Organize files with AI: type a prompt, preview every move, apply. Reads document contents for text-based formats, with rules for automatic file organization.',
  applicationCategory: 'UtilitiesApplication',
  operatingSystem: 'macOS, Windows',
  url: CANONICAL,
  downloadUrl: 'https://www.getsortio.com/download',
  author: {
    '@type': 'Organization',
    name: 'CMG Labs LLC',
    url: 'https://www.getsortio.com',
  },
  offers: [
    {
      '@type': 'Offer',
      price: '0',
      priceCurrency: 'USD',
      name: 'Free',
      description:
        'One-time AI trial allowance, unlimited rule-based sorting, semantic search, and chat',
      availability: 'https://schema.org/InStock',
      url: 'https://www.getsortio.com/download',
    },
    {
      '@type': 'Offer',
      price: '14.99',
      priceCurrency: 'USD',
      name: 'Pro Monthly',
      description: 'Monthly AI allowance, watch folders, on-change automations, commercial license',
      availability: 'https://schema.org/InStock',
      url: 'https://www.getsortio.com/download',
    },
    {
      '@type': 'Offer',
      price: '99',
      priceCurrency: 'USD',
      name: 'Pro Annual',
      description: 'Pro features billed yearly',
      availability: 'https://schema.org/InStock',
      url: 'https://www.getsortio.com/download',
    },
  ],
};

const faqSchema = {
  '@context': 'https://schema.org',
  '@type': 'FAQPage',
  mainEntity: FAQ.map((f) => ({
    '@type': 'Question',
    name: f.question,
    acceptedAnswer: { '@type': 'Answer', text: f.answer },
  })),
};

const breadcrumbSchema = {
  '@context': 'https://schema.org',
  '@type': 'BreadcrumbList',
  itemListElement: [
    { '@type': 'ListItem', position: 1, name: 'Home', item: 'https://www.getsortio.com/' },
    { '@type': 'ListItem', position: 2, name: 'AI File Organizer', item: CANONICAL },
  ],
};

const AiFileOrganizerPage = () => {
  const downloadClick = (ctaId) => () =>
    fireEvent('cta_click', { cta_id: ctaId, page: '/ai-file-organizer', target: '/download' });

  return (
    <div className="min-h-screen bg-black text-apple-100">
      <SEOHead
        title="AI File Organizer for Mac and Windows | Sortio"
        description="Sortio is an AI file organizer: type a prompt, preview every move, apply. Organize files with AI by content, not just filename. Free download for Mac and Windows."
        canonical={CANONICAL}
        schema={[softwareSchema, faqSchema, breadcrumbSchema]}
        keywords="ai file organizer, organize files with ai, automatic file organization, ai file sorter, file organizer software"
        lastModified={LAST_UPDATED}
      />

      <div className="container mx-auto px-6 max-w-4xl py-16 lg:py-24 relative z-10">
        <Breadcrumbs items={[{ label: 'AI File Organizer', href: null }]} />

        {/* Hero */}
        <div className="mb-14">
          <div className="w-14 h-14 rounded-full bg-apple-700/60 backdrop-blur-sm flex items-center justify-center mb-6 border border-apple-600/30">
            <Sparkles className="w-6 h-6 text-sortio-primary" />
          </div>
          <h1 className="text-4xl lg:text-5xl font-semibold mb-6 text-apple-50">
            The AI file organizer that reads your files
          </h1>
          <p className="text-apple-300 text-lg leading-relaxed mb-8">
            Drop a folder. Type one prompt. Sortio renames every file and files it where it belongs,
            with a preview before anything moves and one-click undo after. Free download for Mac and Windows.
          </p>
          <div className="flex flex-col sm:flex-row gap-4">
            <Link
              to="/download"
              onClick={downloadClick('ai_file_organizer_hero')}
              className="inline-flex items-center justify-center px-8 py-4 bg-sortio-primary hover:bg-sortio-primary/90 text-white font-medium rounded-xl transition-colors duration-200"
            >
              Download Sortio Free
              <ArrowRight className="ml-2" size={18} />
            </Link>
            <Link
              to="/pricing"
              className="inline-flex items-center justify-center px-8 py-4 bg-apple-700/40 hover:bg-apple-600/40 text-apple-100 font-medium rounded-xl border border-apple-600/30 transition-colors duration-200"
            >
              See pricing
            </Link>
          </div>
          <p className="text-apple-400 text-sm mt-4">
            No credit card required. One-time AI trial allowance included; rule-based sorting is free and unlimited.
          </p>
        </div>

        {/* What is an AI file organizer */}
        <section className="mb-14">
          <h2 className="text-2xl font-semibold text-apple-50 mb-6">What is an AI file organizer?</h2>
          <div className="space-y-4 text-apple-200 leading-relaxed">
            <p>
              An AI file organizer is software that sorts, renames, and files documents using a language
              model instead of hand-written rules. Traditional tools like Hazel or File Juggler make you
              spell out every pattern: "if the name contains invoice and the extension is .pdf, move it
              here." That works until the files stop matching the pattern, which real files do constantly.
            </p>
            <p>
              With an AI organizer you describe the outcome instead: "organize these by client, then by
              document type, and rename everything to date_client_doctype." The model looks at each file,
              its name, its metadata, and optionally its contents, and decides where it belongs. Files that
              would never match a rule, like a bank statement named statement-final(3).pdf or a contract
              saved as Scan_0042 with a useful text layer, get filed correctly anyway.
            </p>
            <p>
              Sortio is built around that idea, with the safety rails the category needs: every sort shows
              a preview before any file moves, every run can be undone, and AI sorting is capped at 5,000
              files per run so large jobs stay reviewable. For the repeatable patterns, an AI Rule Builder
              turns a plain-English description into a deterministic rule that runs automatically and
              does not use your AI allowance at all, which is how automatic file organization works day to day.
            </p>
          </div>
        </section>

        {/* How it works */}
        <section className="mb-14">
          <h2 className="text-2xl font-semibold text-apple-50 mb-6">How Sortio organizes files with AI</h2>
          <div className="grid gap-4">
            {[
              {
                step: '1',
                title: 'Point it at a folder',
                body: 'Downloads, Desktop, a client dump, a 4,000-file backlog. Sortio indexes names and metadata locally.',
              },
              {
                step: '2',
                title: 'Type a prompt',
                body: '"Sort these by client, then by document type. Rename to YYYY-MM-DD_client_doctype." Plain English, any structure you want. Enable content reading and Sortio also considers what each text-based document actually says.',
              },
              {
                step: '3',
                title: 'Preview, then apply',
                body: 'Sortio shows the full plan: every rename, every destination. Approve it and the files move. Change your mind and one click reverts the whole run.',
              },
            ].map((s) => (
              <div key={s.step} className="flex items-start gap-4 bg-apple-700/30 rounded-2xl border border-apple-600/30 p-6">
                <div className="w-8 h-8 bg-sortio-primary/20 text-sortio-primary rounded-full flex items-center justify-center font-semibold flex-shrink-0">
                  {s.step}
                </div>
                <div>
                  <h3 className="font-medium text-apple-50 mb-1">{s.title}</h3>
                  <p className="text-apple-300 leading-relaxed">{s.body}</p>
                </div>
              </div>
            ))}
          </div>
        </section>

        {/* Feature proof */}
        <section className="mb-14">
          <h2 className="text-2xl font-semibold text-apple-50 mb-6">What you get</h2>
          <div className="grid md:grid-cols-2 gap-4">
            {FEATURES.map((f) => (
              <div key={f.title} className="bg-apple-700/20 rounded-2xl border border-apple-600/30 p-6">
                <f.icon className="text-sortio-primary mb-3" size={22} />
                <h3 className="font-medium text-apple-50 mb-2">{f.title}</h3>
                <p className="text-apple-300 text-sm leading-relaxed">{f.body}</p>
              </div>
            ))}
          </div>
        </section>

        {/* Honest scope */}
        <section className="mb-14">
          <div className="bg-apple-700/20 border border-apple-600/30 rounded-2xl p-6">
            <h2 className="text-xl font-semibold text-apple-50 mb-3">What it does not do</h2>
            <p className="text-apple-300 leading-relaxed">
              Honest scope, because switchers test these on day one: Sortio does not OCR image-only scans
              (a scan with no text layer is sorted by filename and metadata, so run OCR first if content
              matters), AI sorting tops out at 5,000 files per run, and watch folders require Pro. If all
              you need is extension-based tidying, a one-click sorter is cheaper; Sortio earns its keep
              when filenames are useless and the routing depends on what the document is.
            </p>
          </div>
        </section>

        {/* Pricing strip */}
        <section className="mb-14">
          <h2 className="text-2xl font-semibold text-apple-50 mb-6">Pricing</h2>
          <div className="grid md:grid-cols-2 gap-4">
            <div className="bg-apple-700/30 rounded-2xl border border-apple-600/30 p-6">
              <h3 className="font-medium text-apple-50 mb-2">Free</h3>
              <p className="text-apple-300 text-sm leading-relaxed mb-4">
                A one-time AI trial allowance, plus unlimited rule-based sorting,
                semantic search, and file chat. Enough to test on a real folder.
              </p>
              <Link
                to="/download"
                onClick={downloadClick('ai_file_organizer_pricing_free')}
                className="inline-flex items-center text-sortio-primary hover:text-sortio-primary/80 text-sm font-medium"
              >
                Download free
                <ArrowRight className="ml-1" size={14} />
              </Link>
            </div>
            <div className="bg-sortio-primary/10 rounded-2xl border border-sortio-primary/20 p-6">
              <h3 className="font-medium text-apple-50 mb-2">Pro: $14.99/mo or $99/yr</h3>
              <p className="text-apple-300 text-sm leading-relaxed mb-4">
                A monthly AI allowance sized for heavy use, watch folders, on-change automations, commercial
                license. Download free, sign in, and upgrade inside the app in one click.
              </p>
              <Link to="/pricing" className="inline-flex items-center text-sortio-primary hover:text-sortio-primary/80 text-sm font-medium">
                Full pricing details
                <ArrowRight className="ml-1" size={14} />
              </Link>
            </div>
          </div>
        </section>

        {/* FAQ */}
        <section className="mb-14">
          <h2 className="text-2xl font-semibold text-apple-50 mb-6 flex items-center">
            <HelpCircle className="text-sortio-primary mr-3" size={24} />
            Frequently asked questions
          </h2>
          <div className="space-y-4">
            {FAQ.map((f, i) => (
              <div key={i} className="bg-apple-700/20 rounded-xl p-6">
                <h3 className="font-medium text-apple-50 mb-3">{f.question}</h3>
                <p className="text-apple-300 leading-relaxed">{f.answer}</p>
              </div>
            ))}
          </div>
        </section>

        {/* Keep reading */}
        <section className="mb-14">
          <h2 className="text-2xl font-semibold text-apple-50 mb-6 flex items-center">
            <BookOpen className="text-sortio-primary mr-3" size={24} />
            Keep reading
          </h2>
          <div className="grid gap-3">
            {[
              { label: 'Best AI file organizers in 2026, compared', href: '/blog/best-ai-file-organizers' },
              { label: 'Hazel vs Sortio: rules vs AI, head to head', href: '/hazel-vs-sortio' },
              { label: 'AI Sort vs Rule Builder: which to use when', href: '/blog/ai-sort-vs-rule-builder-which-to-use' },
              { label: 'Local AI vs cloud AI: what leaves your machine', href: '/blog/local-ai-vs-cloud-ai-file-organization' },
              { label: 'All comparisons', href: '/compare' },
            ].map((l) => (
              <Link
                key={l.href}
                to={l.href}
                className="apple-panel p-4 rounded-apple group hover:ring-1 hover:ring-sortio-primary/30 transition-all flex items-center justify-between"
              >
                <span className="text-apple-100 group-hover:text-sortio-primary transition-colors">{l.label}</span>
                <ArrowRight className="w-4 h-4 text-apple-400 group-hover:text-sortio-primary group-hover:translate-x-1 transition-all" />
              </Link>
            ))}
          </div>
        </section>

        {/* Final CTA */}
        <section>
          <div className="bg-gradient-to-r from-sortio-primary/10 to-sortio-secondary/10 rounded-2xl p-8 border border-sortio-primary/20">
            <div className="flex items-center mb-4">
              <CheckCircle className="text-sortio-primary mr-3" size={24} />
              <h2 className="text-2xl font-semibold text-apple-50">Try it on your messiest folder</h2>
            </div>
            <p className="text-apple-300 mb-6 leading-relaxed">
              The free tier is enough to run AI sort on a real folder and see the preview-apply-undo loop
              for yourself. No credit card required.
            </p>
            <Link
              to="/download"
              onClick={downloadClick('ai_file_organizer_footer')}
              className="inline-flex items-center px-6 py-3 bg-sortio-primary hover:bg-sortio-primary/90 text-white font-medium rounded-xl transition-colors duration-200"
            >
              Download Sortio Free
              <ArrowRight className="ml-2" size={16} />
            </Link>
          </div>
        </section>
      </div>
    </div>
  );
};

export default AiFileOrganizerPage;
